import type { EventLogV2Payload } from 'src/types/requests/eventslog/events_log';

type PayloadRecord = Record<string, unknown>;

const COVER_CRITICAL_ERRORS = ['obstruction', 'safety_switch', 'bad_feedback', 'cal_abort'];
const SWITCH_ERROR_KEYS = ['overpower', 'overtemp', 'overvoltage', 'undervoltage'];

/** EVENT_KEYS-aligned field order (after deviceId/tag) for flat UEV objects. */
const FLAT_EVENT_FIELDS: Record<number, string[]> = {
	1: ['channel', 'turn', 'reason'],
	2: ['channel'],
	3: ['tmp'],
	4: ['reason', 'temperature', 'battery', 'alarm_hum'],
	5: ['channel', 'state', 'position'],
	6: ['tstamp', 'rootname'],
	7: ['reason', 'state', 'battery', 'luxury', 'illumination', 'tilt', 'vibration', 'temperature'],
	8: ['channel', 'brightness'],
	9: ['alarm_gas'],
	10: ['motion', 'battery', 'luxury', 'illumination', 'temperature'],
	12: ['channel', 'smoke'],
	13: ['channel', 'state', 'component'],
	14: ['reason', 'state', 'battery', 'luxury', 'tilt'],
	15: ['motion', 'battery', 'luxury'],
	16: ['temperature', 'alarm_hum', 'battery', 'illumination'],
	17: ['module', 'value'],
	18: ['lock'],
	19: ['state', 'distance', 'battery'],
	20: ['ref', 'state', 'num_objects'],
	21: ['channel', 'turn', 'vial_level'],
	22: ['camerazone', 'motion', 'recordingId', 'imageId'],
	23: ['turn', 'reason'],
};

/** Aliases from UEV DOCS / snapshot field names → EVENT_KEYS slot name. */
const FIELD_ALIASES: Record<string, string[]> = {
	channel: ['channel'],
	turn: ['turn', 'output', 'on', 'state'],
	tmp: ['tmp', 'temperature', 'tC'],
	temperature: ['temperature', 'tmp', 'tC'],
	reason: ['reason', 'source', 'src'],
	battery: ['battery', 'bat'],
	alarm_hum: ['alarm_hum', 'humidity', 'hum', 'rh'],
	state: ['state', 'door', 'vibration', 'output', 'on', 'bolt_state', 'lock'],
	position: ['position', 'current_pos', 'apower'],
	tstamp: ['tstamp', 'timestamp'],
	rootname: ['rootname', 'name', 'sceneId'],
	luxury: ['luxury', 'lux_value', 'lux', 'illuminance'],
	illumination: ['illumination', 'lux_illumination'],
	tilt: ['tilt', 'rotation'],
	vibration: ['vibration'],
	brightness: ['brightness'],
	alarm_gas: ['alarm_gas', 'gas'],
	motion: ['motion', 'is_active'],
	smoke: ['smoke', 'alarm'],
	component: ['component', 'ref'],
	module: ['module', 'ref', 'component'],
	value: ['value', 'output', 'state', 'event'],
	lock: ['lock', 'bolt_state', 'state'],
	distance: ['distance'],
	ref: ['ref', 'presence_zone', 'zone', 'component'],
	num_objects: ['num_objects'],
	vial_level: ['vial_level', 'level'],
	camerazone: ['camerazone', 'zone'],
	recordingId: ['recordingId', 'recId', 'rec_id'],
	imageId: ['imageId', 'mediaId', 'media_id'],
};

const FLAT_HINT_KEYS = [
	'deviceId',
	'sceneId',
	'channel',
	'turn',
	'brightness',
	'recId',
	'mediaId',
	'recordingId',
	'imageId',
	'reason',
	'motion',
	'tmp',
	'bat',
	'state',
	'output',
	'on',
	'lock',
	'ref',
	'num_objects',
	'vial_level',
	'level',
	'camerazone',
	'zone',
	'alarm_gas',
	'smoke',
	'distance',
	'tstamp',
	'timestamp',
];

const pickField = (source: PayloadRecord, ...keys: string[]): unknown => {
	for (const key of keys) {
		const value = source[key];
		if (value !== undefined && value !== null) {
			return value;
		}
	}
	return undefined;
};

/**
 * True when `p` is a flat keyed UEV object (not a cs/ps or current/previous snapshot).
 * Payloads with `detected_from` use the snapshot normalizers so nested sensor fields are kept.
 */
export const isFlatKeyedEventLogPayload = (payload: EventLogV2Payload): boolean => {
	if (payload.current || payload.previous || payload.cs || payload.ps || payload.detected_from) {
		return false;
	}
	return FLAT_HINT_KEYS.some((key) => payload[key] !== undefined && payload[key] !== null);
};

const resolveTag = (payload: EventLogV2Payload, fallbackTag: string, eventId: number): string => {
	if (typeof payload.deviceId === 'string' && payload.deviceId.length > 0) {
		return payload.deviceId;
	}
	if (eventId === 6 && typeof payload.sceneId === 'string' && payload.sceneId.length > 0) {
		return payload.sceneId;
	}
	if (eventId !== 6 && fallbackTag.includes('_')) {
		return fallbackTag.split('_')[0];
	}
	return fallbackTag;
};

const pickFlatField = (payload: EventLogV2Payload, slot: string): unknown => {
	const aliases = FIELD_ALIASES[slot] || [slot];
	return pickField(payload as PayloadRecord, ...aliases);
};

const normalizeFlatCriticalError = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const channel = (pickFlatField(payload, 'channel') as number | undefined) ?? 0;
	const explicitError = pickField(payload as PayloadRecord, 'error');

	if (typeof explicitError === 'string' && explicitError.length > 0) {
		const errorValue = payload[explicitError];
		return errorValue !== undefined && errorValue !== null
			? [tag, channel, explicitError, errorValue]
			: [tag, channel, explicitError];
	}

	for (const errorKey of [...SWITCH_ERROR_KEYS, ...COVER_CRITICAL_ERRORS]) {
		const errorValue = payload[errorKey];
		if (errorValue !== undefined && errorValue !== null && errorValue !== false) {
			return typeof errorValue === 'boolean'
				? [tag, channel, errorKey]
				: [tag, channel, errorKey, errorValue];
		}
	}

	const activeErrors = payload.errors;
	if (Array.isArray(activeErrors) && activeErrors.length > 0) {
		const errorKey = String(activeErrors[0]);
		const errorValue = payload[errorKey];
		return errorValue !== undefined
			? [tag, channel, errorKey, errorValue]
			: [tag, channel, errorKey];
	}

	return [tag, channel];
};

/**
 * Maps a flat keyed UEV `p` object to a v1 positional array
 * `[tagOrDeviceId, ...EVENT_KEYS-ordered values]`.
 */
export const normalizeFlatEventLogPayload = (
	eventId: number,
	payload: EventLogV2Payload,
	fallbackTag: string,
): unknown[] | null => {
	const tag = resolveTag(payload, fallbackTag, eventId);

	if (eventId === 11) {
		return normalizeFlatCriticalError(tag, payload);
	}

	const fields = FLAT_EVENT_FIELDS[eventId];
	if (!fields) {
		return null;
	}

	const values = fields.map((slot) => pickFlatField(payload, slot));

	if (eventId === 6) {
		const filtered = values.filter((value) => value !== undefined);
		return [tag, ...filtered];
	}

	if (eventId === 20) {
		const [ref, state, numObjects] = values;
		if (ref !== undefined || state !== undefined || numObjects !== undefined) {
			return [tag, ref, state, numObjects];
		}
	}

	if (eventId === 23) {
		const turn = pickField(payload as PayloadRecord, 'turn', 'output', 'on', 'state');
		const reason = pickField(payload as PayloadRecord, 'reason', 'source', 'src');
		return [tag, turn, reason];
	}

	return [tag, ...values];
};
